import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import FullWidthContainer from 'components/consumer/FullWidthContainer';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import ManualInputSlider from './components/ManualInputSlider';
import CustomerPhotosSlider from './components/CustomerPhotosSlider';
import CardCarousel from './components/CardCarousel';
import EmblaCarousel from './components/EmblaCarousel';

const ImageSliderStandard = props => {
  const {
    arrowPosition = 'standard',
    disableSliding = false,
    fabricGridControls = false,
    fullWidth,
    hideArrowsIfAllSlidesVisible = false,
    isMobileMaterialSwatches = false,
    variant,
  } = props;

  const { imageArray, imgArray, variation, children } = props.config;
  const finalImageArray = imgArray || imageArray || null;
  const [currentImgSet, setImageSet] = useState(0);

  const [finalImageDisplay, updateImageDisplay] = useState(() => {
    if (!finalImageArray) return null;
    if (finalImageArray.length > 50 && currentImgSet === 0) {
      return finalImageArray.slice(0, 50);
    }
    return [...finalImageArray];
  });

  const [slideIndex, setSlideIndex] = useState(0);

  let visibleSlides = props.config.visibleSlides || 1;
  let navType = props.config.navType || 'Button';
  const type = '';
  let logo = props.config.logo || '';
  let title = props.config.title || '';
  let description = props.config.description || '';
  let imageWidth = 340;
  let imageHeight = 400;
  let infinite = false;
  let deviceOverride = false;
  let beforeSlideFn = null;
  let afterSlideFn = null;
  let fullHeight = false;
  useEffect(() => {
    if (!currentImgSet || finalImageDisplay.length < 50 || !finalImageArray)
      return;
    // Only for image sets larger than 50
    if (
      currentImgSet > 0 &&
      finalImageDisplay.length >= 50 &&
      finalImageArray &&
      finalImageArray.length > finalImageDisplay.length * (currentImgSet + 1) &&
      finalImageArray.length > finalImageDisplay.length
    ) {
      const newlength = finalImageDisplay.length * (currentImgSet + 1);
      updateImageDisplay(
        finalImageDisplay.concat(
          finalImageArray.slice(finalImageDisplay.length, newlength)
        )
      );
    } else if (
      currentImgSet > 0 &&
      finalImageDisplay.length >= 50 &&
      finalImageArray &&
      finalImageArray.length < finalImageDisplay.length * (currentImgSet + 1) &&
      finalImageArray.length > finalImageDisplay.length
    ) {
      updateImageDisplay(
        finalImageDisplay.concat(
          finalImageArray.slice(finalImageDisplay.length)
        )
      );
    }
  }, [currentImgSet, finalImageArray, finalImageDisplay]);

  const updateImageArraySize = updatedSlideIndex => {
    setImageSet(currentImgSet + 1);
    setSlideIndex(updatedSlideIndex);
  };

  if (props.config.sliderConfig) {
    const { sliderConfig } = props.config;
    visibleSlides = sliderConfig.visibleSlides;
    navType = sliderConfig.navType;
    logo = sliderConfig.logo;
    title = sliderConfig.title;
    description = sliderConfig.description;
    imageWidth = sliderConfig.imageWidth;
    imageHeight = sliderConfig.imageHeight;
    infinite = sliderConfig.infinite || false;
    deviceOverride = sliderConfig.deviceOverride || false;
    beforeSlideFn = sliderConfig.beforeSlideFn;
    afterSlideFn = sliderConfig.afterSlideFn;
    fullHeight = sliderConfig.fullHeight;
  }

  if (!finalImageArray && !children) return null;

  const ImageSliderContainer = fullWidth
    ? FullWidthContainer
    : FixedWidthContainer;

  return (
    <ImageSliderContainer noMobilePadding>
      <div
        className={classNames('ImageSliderStandard__container', {
          '[&_.slider-control-centerleft]:!left-[3px] [&_.slider-control-centerright]:!right-[3px]':
            arrowPosition === 'standard',
          '[&_.slider-control-centerleft]:!left-8 [&_.slider-control-centerright]:!right-8':
            arrowPosition === 'inset',
        })}
      >
        {!variation || variation.value === 'Manual' ? (
          <ManualInputSlider
            hideArrowsIfAllSlidesVisible={hideArrowsIfAllSlidesVisible}
            imageArray={finalImageDisplay}
            imageHeight={imageHeight}
            imageWidth={imageWidth}
            infinite={infinite}
            navType={navType}
            slideIndex={slideIndex}
            updateImageArraySize={updateImageArraySize}
            visibleSlides={visibleSlides}
            variant={variant}
          />
        ) : null}

        {variation && variation.value === 'Customer Photos' ? (
          <CustomerPhotosSlider
            imageArray={finalImageArray}
            type={type || 'General'}
            logo={logo}
            description={description}
            title={title}
            visibleSlides={visibleSlides}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            navType={navType}
            updateImageArraySize={updateImageArraySize}
            slideIndex={slideIndex}
            variant={variant}
          />
        ) : null}

        {!isNewConsumerApp &&
        variation &&
        variation.value === 'Component' &&
        children ? (
          <CardCarousel
            visibleSlides={visibleSlides}
            imageWidth={imageWidth || 400}
            imageHeight={imageHeight || 400}
            navType={navType}
            deviceOverride={deviceOverride}
            afterSlideFn={afterSlideFn}
            beforeSlideFn={beforeSlideFn}
            fabricGridControls={fabricGridControls}
            isMobileMaterialSwatches={isMobileMaterialSwatches}
            disableSliding={disableSliding}
            slideIndex={props.config.sliderConfig.sliderIndex}
            variant={variant}
            fullHeight={fullHeight}
          >
            {children}
          </CardCarousel>
        ) : null}

        {isNewConsumerApp &&
        variation &&
        variation.value === 'Component' &&
        children ? (
          <EmblaCarousel
            carouselConfig={{ loop: true, align: 'center' }}
            variant={variant}
            slideIndexFn={afterSlideFn}
          >
            {children}
          </EmblaCarousel>
        ) : null}
      </div>
    </ImageSliderContainer>
  );
};

ImageSliderStandard.propTypes = {
  arrowPosition: PropTypes.oneOf(['standard', 'inset']),
  config: PropTypes.shape({
    children: PropTypes.node,
    description: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    imageArray: PropTypes.any,
    imgArray: PropTypes.array,
    logo: PropTypes.string,
    navType: PropTypes.string,
    sliderConfig: PropTypes.shape({
      afterSlideFn: PropTypes.func,
      beforeSlideFn: PropTypes.func,
      description: PropTypes.string,
      deviceOverride: PropTypes.bool,
      imageHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      imageWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      infinite: PropTypes.bool,
      logo: PropTypes.string,
      navType: PropTypes.string,
      title: PropTypes.string,
      visibleSlides: PropTypes.number,
      sliderIndex: PropTypes.number,
      fullHeight: PropTypes.bool,
    }),
    title: PropTypes.string,
    variation: PropTypes.shape({
      value: PropTypes.string,
    }),
    visibleSlides: PropTypes.number,
  }),
  fabricGridControls: PropTypes.bool,
  fullWidth: PropTypes.bool,
  hideArrowsIfAllSlidesVisible: PropTypes.bool,
  isMobileMaterialSwatches: PropTypes.bool,
  disableSliding: PropTypes.bool,
  variant: PropTypes.oneOf(['material-swatches-sidebar', 'reviews-list']),
};

const ImageSliderStandardUI = props => {
  if (props?.config?.elementAttribute?.id) {
    return (
      <div id={props?.config?.elementAttribute?.id}>
        <ImageSliderStandard {...props} />
      </div>
    );
  }
  return <ImageSliderStandard {...props} />;
};

ImageSliderStandardUI.propTypes = {
  arrowPosition: PropTypes.oneOf(['standard', 'inset']),
  config: PropTypes.shape({
    children: PropTypes.node,
    description: PropTypes.string,
    elementAttribute: PropTypes.shape({
      id: PropTypes.number,
    }),
    // eslint-disable-next-line react/forbid-prop-types
    imageArray: PropTypes.any,
    imgArray: PropTypes.array,
    logo: PropTypes.string,
    navType: PropTypes.string,
    sliderConfig: PropTypes.shape({
      afterSlideFn: PropTypes.func,
      beforeSlideFn: PropTypes.func,
      description: PropTypes.string,
      deviceOverride: PropTypes.bool,
      imageHeight: PropTypes.number,
      imageWidth: PropTypes.number,
      infinite: PropTypes.bool,
      logo: PropTypes.string,
      navType: PropTypes.string,
      title: PropTypes.string,
      visibleSlides: PropTypes.number,
      fullHeight: PropTypes.bool,
    }),
    title: PropTypes.string,
    variation: PropTypes.shape({
      value: PropTypes.string,
    }),
    visibleSlides: PropTypes.number,
  }),
  fabricGridControls: PropTypes.bool,
  fullWidth: PropTypes.bool,
  hideArrowsIfAllSlidesVisible: PropTypes.bool,
  isMobileMaterialSwatches: PropTypes.bool,
  disableSliding: PropTypes.bool,
  variant: PropTypes.oneOf(['material-swatches-sidebar', 'reviews-list']),
};

export default ImageSliderStandardUI;
